// note-draft.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface NoteDraft {
  id: string;
  leadId: string;
  userId: string;
  content: string;
  timestamp: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class NoteDraftService {
  private dbName = 'NotesDraftDB';
  private version = 1;
  private db: IDBDatabase | null = null;
  private storeName = 'drafts';
  private cleanupInterval: any;
  
  // Subject to notify components about draft changes
  private draftSubject = new BehaviorSubject<NoteDraft | null>(null);
  public draft$ = this.draftSubject.asObservable();

  constructor() {
    this.initDatabase();
    this.startCleanupJob();
  }

  private async initDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          
          // Create indexes
          store.createIndex('leadId', 'leadId', { unique: false });
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
          store.createIndex('composite', ['leadId', 'userId'], { unique: true });
        }
      };
    });
  }

  // Generate unique ID for draft
  private generateDraftId(leadId: string, userId: string): string {
    return `draft_${leadId}_${userId}`;
  }

  // Save or update draft
  async saveDraft(leadId: string, userId: string, content: string): Promise<void> {
    if (!this.db) {
      await this.initDatabase();
    }

    // Don't save empty drafts
    if (!content || !content.trim()) {
      // If content is empty, delete any existing draft
      await this.deleteDraft(leadId, userId);
      return;
    }

    const now = Date.now();
    const oneHourLater = now + (60 * 60 * 1000); // 1 hour in milliseconds

    const draft: NoteDraft = {
      id: this.generateDraftId(leadId, userId),
      leadId,
      userId,
      content,
      timestamp: now,
      expiresAt: oneHourLater
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(draft);

      request.onsuccess = () => {
        this.draftSubject.next(draft);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to save draft:', request.error);
        reject(request.error);
      };
    });
  }

  // Get draft for specific lead and user
  async getDraft(leadId: string, userId: string): Promise<NoteDraft | null> {
    if (!this.db) {
      await this.initDatabase();
    }

    const draftId = this.generateDraftId(leadId, userId);

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(draftId);

      request.onsuccess = () => {
        const draft = request.result;
        
        if (draft) {
          // Check if draft has expired
          if (draft.expiresAt < Date.now()) {
            // Delete expired draft
            this.deleteDraft(leadId, userId).then(() => {
              resolve(null);
            });
          } else {
            this.draftSubject.next(draft);
            resolve(draft);
          }
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('Failed to get draft:', request.error);
        reject(request.error);
      };
    });
  }

  // Delete draft
  async deleteDraft(leadId: string, userId: string): Promise<void> {
    if (!this.db) {
      await this.initDatabase();
    }

    const draftId = this.generateDraftId(leadId, userId);

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(draftId);

      request.onsuccess = () => {
        this.draftSubject.next(null);
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to delete draft:', request.error);
        reject(request.error);
      };
    });
  }

  // Get all drafts for a user
  async getUserDrafts(userId: string): Promise<NoteDraft[]> {
    if (!this.db) {
      await this.initDatabase();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => {
        const drafts = request.result.filter(draft => draft.expiresAt > Date.now());
        resolve(drafts);
      };

      request.onerror = () => {
        console.error('Failed to get user drafts:', request.error);
        reject(request.error);
      };
    });
  }

  // Clean up expired drafts
  private async cleanupExpiredDrafts(): Promise<void> {
    if (!this.db) {
      await this.initDatabase();
    }

    const now = Date.now();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('expiresAt');
      const range = IDBKeyRange.upperBound(now);
      const request = index.openCursor(range);

      let deletedCount = 0;

      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          if (deletedCount > 0) {
          }
          resolve();
        }
      };

      request.onerror = () => {
        console.error('Failed to cleanup drafts:', request.error);
        reject(request.error);
      };
    });
  }

  // Start cleanup job that runs every 5 minutes
  private startCleanupJob(): void {
    // Initial cleanup
    setTimeout(() => {
      this.cleanupExpiredDrafts();
    }, 5000); // Wait 5 seconds after initialization

    // Run cleanup every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredDrafts();
    }, 5 * 60 * 1000);
  }

  // Clean up when service is destroyed
  ngOnDestroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    
    if (this.db) {
      this.db.close();
    }
  }
}